<script>
  import { onMount } from 'svelte';
  import { getFavoriteHomeIds, toggleHomeFavorite } from '../utils/favorites';

  export let data = [];

  let items = [];
  let filteredItems = [];
  let favoriteIds = new Set();

  let filters = {
    levels: '',
    size: '',
    style: '',
    favoritesOnly: false,
    title: new Set()
  };

  let levelOptions = [];
  let sizeOptions = [];
  let styleOptions = [];

  function normalizeScalar(value) {
    if (value == null) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).trim();
    }

    if (typeof value === 'object') {
      if (value.value != null && String(value.value).trim()) {
        return String(value.value).trim();
      }

      if (value.label != null && String(value.label).trim()) {
        return String(value.label).trim();
      }
    }

    return '';
  }

  function normalizeStyleList(styleValue) {
    if (!styleValue) {
      return [];
    }

    const values = Array.isArray(styleValue) ? styleValue : [styleValue];

    return values
      .map((entry) => normalizeScalar(entry))
      .filter(Boolean);
  }

  onMount(() => {
    const el = document.getElementById('svelte-gallery');

    if (el && el.dataset && el.dataset.gallery) {
      const raw = JSON.parse(el.dataset.gallery);

      items = raw.map(item => ({
        id: item.id ? String(item.id) : '',
        title: item.title,
        permalink: item.permalink,
        thumbnailSrc: item.thumbnailSrc || item.thumbnail,
        thumbnailSrcset: item.thumbnailSrcset || '',
        thumbnailSizes: item.thumbnailSizes || '(max-width: 640px) 100vw, (max-width: 1200px) 50vw, 480px',
        levels: normalizeScalar(item.levels),
        size: normalizeScalar(item.size),
        style: normalizeStyleList(item.style)
      }));

      const levelSet = new Set();
      const sizeSet = new Set();
      const styleSet = new Set();

      items.forEach(item => {
        if (item.levels) levelSet.add(item.levels);
        if (item.size) sizeSet.add(item.size);
        item.style.forEach(s => styleSet.add(s));
      });

      levelOptions = Array.from(levelSet).sort((a, b) => a.localeCompare(b));
      sizeOptions = Array.from(sizeSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      styleOptions = Array.from(styleSet).sort((a, b) => a.localeCompare(b));
    }

    syncFavorites();

    window.addEventListener('aubuchon:favorites-updated', syncFavorites);
    window.addEventListener('storage', syncFavorites);

    return () => {
      window.removeEventListener('aubuchon:favorites-updated', syncFavorites);
      window.removeEventListener('storage', syncFavorites);
    };
  });

  function syncFavorites() {
    favoriteIds = new Set(getFavoriteHomeIds());
    applyFilters();
  }

  function isFavorite(homeId) {
    return !!homeId && favoriteIds.has(String(homeId));
  }

  function toggleFavorite(event, homeId) {
    event.preventDefault();
    event.stopPropagation();

    if (!homeId) {
      return;
    }

    const result = toggleHomeFavorite(homeId);
    favoriteIds = new Set(result.favorites);
    applyFilters();
  }

  function toggleFavoritesOnly() {
    filters = {
      levels: filters.levels,
      size: filters.size,
      style: filters.style,
      title: filters.title,
      favoritesOnly: !filters.favoritesOnly
    };
    applyFilters();
  }

  function applyFilters() {
    const activeLevel = normalizeScalar(filters.levels);
    const activeSize = normalizeScalar(filters.size);
    const activeStyle = normalizeScalar(filters.style);

    filteredItems = items.filter(item => {
      const matchLevel = !activeLevel || item.levels === activeLevel;
      const matchSize = !activeSize || item.size === activeSize;
      const matchStyle = !activeStyle || item.style.includes(activeStyle);
      const matchTitle = filters.title.size === 0 || filters.title.has(item.title);
      const matchFavorite = !filters.favoritesOnly || isFavorite(item.id);
      return matchLevel && matchSize && matchStyle && matchTitle && matchFavorite;
    });
  }

  function clearFilters() {
    filters = {
      levels: '',
      size: '',
      style: '',
      favoritesOnly: false,
      title: new Set()
    };
    applyFilters();
  }
</script>

<!-- Filter UI -->
<div class="gallery-filters" style="margin-bottom: 2rem;">

    <span class="filtertitle">Filter Portfolio</span>
  <div class="filtertype">
    <div class="custom-select">
        <select bind:value={filters.levels} on:change={applyFilters}>
        <option value="">Floors</option>
        {#each levelOptions as option}
            <option value={option}>{option}</option>
        {/each}
        </select>
    </div>
  </div>

  <div class="filtertype">
    <div class="custom-select">
        <select bind:value={filters.size} on:change={applyFilters}>
        <option value="">Square Feet</option>
        {#each sizeOptions as option}
            <option value={option}>{option}</option>
        {/each}
        </select>
    </div>
  </div>

  <div class="filtertype">
    <div class="custom-select">
        <select bind:value={filters.style} on:change={applyFilters}>
        <option value="">All Styles</option>
        {#each styleOptions as option}
            <option value={option}>{option}</option>
        {/each}
        </select>
    </div>
  </div>

  <button class:active={filters.favoritesOnly} class="button favorites-only" on:click={toggleFavoritesOnly}>
    {filters.favoritesOnly ? 'Showing Favorites' : 'Favorites Only'}
  </button>

    <button class="button clear" on:click={clearFilters}>Clear Filters</button>

</div>

<!-- Gallery Items -->
<div class="sub-links-wrap">
  {#if filteredItems.length === 0}
    <div class="noresults">
        <p>No Projects Match Your Filters. Please Try Again.</p>
    </div>
  {/if}

  {#each filteredItems as item}
    <div class="gallery-sub-link" class:is-favorite={isFavorite(item.id)}>
      <div class="gal-item-background">
        <img
          class="gal-item-background-image"
          src={item.thumbnailSrc}
          srcset={item.thumbnailSrcset}
          sizes={item.thumbnailSizes}
          alt={item.title}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div class="square-overlay">
        <div class="border-left"></div>
        <div class="border-right"></div>
      </div>
    <button
    type="button"
    class="favorite-heart"
    class:is-favorite={isFavorite(item.id)}
    on:click={(event) => toggleFavorite(event, item.id)}
    aria-label={isFavorite(item.id) ? 'Remove from favorites' : 'Save to favorites'}
    aria-pressed={isFavorite(item.id)}
    title={isFavorite(item.id) ? 'Remove from favorites' : 'Save to favorites'}
    >
    <span aria-hidden="true">{isFavorite(item.id) ? '♥' : '♡'}</span>
    </button>
      <a href={item.permalink}><span class="sr-only">View</span></a>
    </div>
  {/each}
</div>

<style>
    .gallery-filters { 
        background: #000;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 2rem 1rem;
    }
    .noresults { 
        background: #eee;
        padding: 1rem 2rem;
        text-align: center;
        margin: 0px auto;
        font-weight: bold;
        text-transform: uppercase;
        font-size: 1.5rem;
    }

    .filtertype { 
        margin: 0 15px;
        min-width: 170px;
    }
    .custom-select { 
        position: relative;
        width: 100%;
    }
    .custom-select:before { 
        content: url(data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2211%22%20height%3D%227%22%3E%3Cpath%20fill%3D%22%23BB874C%22%20fill-rule%3D%22evenodd%22%20d%3D%22M10.9.5L5.5%206.9.1.5%22%2F%3E%3C%2Fsvg%3E);
        position: absolute;
        display: block;
        width: 11px;
        height: 7px;
        right: 15px;
        top: 12px;
    }
    .custom-select select { 
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        width: 100%;
    }
    .filtertitle {
        text-transform: uppercase;
        color: #c2955b;
        font-size: 1.25rem;
     }
    .dropdown-toggle,
    select {
        cursor: pointer;
        background: #fff;
        border: 1px solid #ccc;
        padding: 1rem 1.3rem 1rem 1rem;
        line-height: 1;
        margin: 0;
        border: 0;
    }
    .filtertype.dropdown { 
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
    }
    .dropdown-menu { 
        position: absolute;
        top: 48px;
        left: 0;
        z-index: 2;
        border: 1px solid #FFF;
        padding: 1rem;
        overflow-y: auto;
        max-height: 350px;
        min-width: 500px;
        background: #000;
        color: #FFF;

    }
    .dropdown-menu label { 
       font-size: 1rem;
       margin-bottom: 10px;
    }
    button.clear { 
        margin: 0 15px;
        padding: .8rem 2rem;
        border: 0;
        text-transform: uppercase; 
    }
    button.favorites-only {
      margin: 0 8px;
      padding: .8rem 1.4rem;
      border: 1px solid #c2955b;
      background: transparent;
      color: #c2955b;
      text-transform: uppercase;
    }
    button.favorites-only.active {
      background: #c2955b;
      color: #fff;
    }

    .gallery-sub-link {
      position: relative;
    }

    .favorite-heart {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 3;
      width: 2.5rem;
      height: 2.5rem;
      border: 1px solid rgba(255, 255, 255, 0.9);
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.35);
      color: #fff;
      font-size: 1.2rem;
      line-height: 1;
      cursor: pointer;
      transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
    }

    .favorite-heart:hover {
      transform: scale(1.08);
      background: rgba(0, 0, 0, 0.55);
    }

    .favorite-heart.is-favorite {
      background: rgba(200, 33, 74, 0.92);
      border-color: rgba(200, 33, 74, 1);
    }

    .gal-item-background-image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    @media (max-width: 1250px) { 
        .gallery-filters { flex-wrap: wrap; }
        .filtertype { 
            flex-grow: 1;
        }
        .filtertitle { 
            width: 100%;
            text-align: center;
            margin-bottom: 1.5rem;
        }
    }
    
    @media (max-width: 920px) { 
        .gallery-filters { 
            justify-content: space-around;
        }
        .filtertype { 
            width: 48%;
            margin-bottom: 1rem;
            min-width: auto;
            margin: 5px;
        }
      button.clear,
      button.favorites-only { width: 100%; margin: 5px; }
    }
</style>